/** Viewport rect for a textarea/input text selection (for floating UI placement). */

const MIRROR_ID = 'textarea-selection-mirror';

function copyInputTypography(source: Element, mirror: HTMLElement): void {
  const style = getComputedStyle(source);
  const keys = [
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'textTransform',
    'wordSpacing',
    'lineHeight',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'boxSizing',
    'width',
  ] as const;
  for (const key of keys) {
    mirror.style[key] = style[key];
  }
  mirror.style.position = 'fixed';
  mirror.style.left = '-9999px';
  mirror.style.top = '0';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordWrap = 'break-word';
  mirror.style.overflow = 'hidden';
}

function getMirror(): HTMLDivElement {
  let el = document.getElementById(MIRROR_ID) as HTMLDivElement | null;
  if (!el) {
    el = document.createElement('div');
    el.id = MIRROR_ID;
    el.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el);
  }
  return el;
}

export function getTextControlSelectionRect(
  control: HTMLTextAreaElement | HTMLInputElement,
  start: number,
  end: number
): DOMRect {
  const lo = Math.min(start, end);
  const hi = Math.max(start, end);
  const value = control.value;
  const before = escapeHtml(value.slice(0, lo));
  const selected = escapeHtml(value.slice(lo, hi)) || ' ';
  const after = escapeHtml(value.slice(hi));

  const controlRect = control.getBoundingClientRect();
  const mirror = getMirror();
  copyInputTypography(control, mirror);
  mirror.style.position = 'fixed';
  mirror.style.left = `${controlRect.left}px`;
  mirror.style.top = `${controlRect.top}px`;
  mirror.style.width = `${controlRect.width}px`;
  mirror.style.height = `${controlRect.height}px`;
  mirror.style.overflow = 'auto';
  mirror.style.visibility = 'hidden';
  mirror.style.opacity = '0';
  mirror.style.pointerEvents = 'none';
  mirror.scrollTop = control.scrollTop;
  mirror.innerHTML = `${before}<mark id="textarea-sel-mark">${selected}</mark>${after}`;

  const mark = mirror.querySelector('#textarea-sel-mark');
  if (!mark) {
    return new DOMRect(
      controlRect.left + 8,
      controlRect.top + 8,
      Math.max(controlRect.width - 16, 24),
      parseFloat(getComputedStyle(control).lineHeight) || 20
    );
  }

  const markRect = mark.getBoundingClientRect();
  if (markRect.width > 0 || markRect.height > 0) {
    return markRect;
  }
  return new DOMRect(
    controlRect.left + 8,
    controlRect.top + 8,
    Math.max(controlRect.width - 16, 24),
    parseFloat(getComputedStyle(control).lineHeight) || 20
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
}

function textOffsetInMirror(mirror: HTMLElement, targetNode: Node, targetOffset: number): number {
  const walker = document.createTreeWalker(mirror, NodeFilter.SHOW_TEXT);
  let total = 0;
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (node === targetNode) return total + targetOffset;
    total += node.textContent?.length ?? 0;
  }
  return total;
}

/** Character index in textarea.value under viewport coordinates. */
export function textareaOffsetAtPoint(
  textarea: HTMLTextAreaElement,
  clientX: number,
  clientY: number
): number | null {
  const box = textarea.getBoundingClientRect();
  if (
    clientX < box.left ||
    clientX > box.right ||
    clientY < box.top ||
    clientY > box.bottom
  ) {
    return null;
  }

  const doc = document;
  if (doc.caretPositionFromPoint) {
    const pos = doc.caretPositionFromPoint(clientX, clientY);
    if (pos?.offsetNode && textarea.contains(pos.offsetNode)) {
      return pos.offset;
    }
  }

  const mirror = getMirror();
  copyInputTypography(textarea, mirror);
  mirror.style.position = 'fixed';
  mirror.style.left = `${box.left}px`;
  mirror.style.top = `${box.top}px`;
  mirror.style.width = `${box.width}px`;
  mirror.style.height = `${box.height}px`;
  mirror.style.overflow = 'auto';
  mirror.style.opacity = '0.01';
  mirror.style.pointerEvents = 'none';
  mirror.style.zIndex = '12999';
  mirror.innerHTML = escapeHtml(textarea.value);
  mirror.scrollTop = textarea.scrollTop;

  const range = doc.caretRangeFromPoint?.(clientX, clientY);
  if (!range || !mirror.contains(range.startContainer)) {
    return null;
  }
  return textOffsetInMirror(mirror, range.startContainer, range.startOffset);
}
